import { getBillingProfiles, getPaymentHistory } from 'api/subscription'
import { getMySubscriptionGroup } from 'api/subscriptionGroups'
import { useAuthContext } from 'components/context/AuthProvider'
import { useQuery, useQueryClient } from 'react-query'
import { IGetMySubscriptionGroupResponse, PaymentHistoryRow, UserSubscription } from 'types'
import { isSubscriptionGated } from '../utils'

const BILLING_PROFILE_KEY = 'billingProfile'
const PAYMENT_HISTORY_KEY = 'paymentHistory'
const SUBSCRIPTION_GROUP_KEY = 'subscriptionGroup'

export function useCurrentSubscription(): UserSubscription | null {
  const { user } = useAuthContext()
  return user?.lastSubscription ?? null
}

export function useBillingProfile() {
  const { user } = useAuthContext()

  const { data, isLoading, refetch } = useQuery(
    [BILLING_PROFILE_KEY],
    async () => {
      const profiles = (await getBillingProfiles()).data
      return (
        profiles.find((profile) => profile.defaultPaymentMethodId !== null) ?? profiles[0] ?? null
      )
    },
    { enabled: isSubscriptionGated(user) },
  )

  return { billingProfile: data ?? null, isLoading, refetch }
}

export function useInvalidateBillingProfile() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries([BILLING_PROFILE_KEY])
}

/**
 * Loads /subscription-groups/my for owners/members of a Family plan.
 * Returns null for caregivers not in a family group — backend still answers, but we keep the hook
 * disabled unless the auth user has familyContext, to avoid an extra request.
 */
export function useMySubscriptionGroup(enabled: boolean) {
  const { data, isLoading, refetch } = useQuery<IGetMySubscriptionGroupResponse>(
    [SUBSCRIPTION_GROUP_KEY],
    async () => (await getMySubscriptionGroup()).data,
    { enabled },
  )

  return { group: data ?? null, isLoading, refetch }
}

export function useInvalidateSubscriptionGroup() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries([SUBSCRIPTION_GROUP_KEY])
}

export function usePaymentHistory() {
  const { user } = useAuthContext()

  const { data, isLoading } = useQuery(
    [PAYMENT_HISTORY_KEY],
    async () => {
      const response = await getPaymentHistory({})
      return response.data.paymentHistory
    },
    { enabled: isSubscriptionGated(user) },
  )

  return { paymentHistory: data ?? ([] as PaymentHistoryRow[]), isLoading }
}
